import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, Loader2, X } from "lucide-react";

/*
 * Landing pages after Stripe hosted Checkout redirects back.
 *
 * SUCCESS: polls /api/payments/status/{session_id} every 2s until Stripe
 * (or our webhook) marks the transaction as paid. Then triggers an auth
 * refresh so the app sees subscription_status=active and the paywall
 * disappears.
 *
 * CANCEL: static apology page with a "try again" link.
 */

export function SubscribeSuccess() {
  const location = useLocation();
  const { user, refresh } = useAuth();
  const sessionId = new URLSearchParams(location.search).get("session_id");
  const [state, setState] = useState("polling"); // "polling" | "paid" | "failed"
  const pollCount = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      pollCount.current += 1;
      // Refresh /auth/me on every tick — Stripe's webhook may have landed
      // while we were polling the payment_transactions record. If either
      // signal flips (transaction paid OR user's subscription active), we
      // stop and unlock the dashboard.
      let userStatus = null;
      try {
        const refreshed = await refresh?.();
        userStatus = refreshed?.user?.subscription_status ?? null;
      } catch { /* non-fatal */ }
      const unlocked =
        userStatus === "active" ||
        userStatus === "trialing" ||
        userStatus === "past_due";

      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid" || unlocked) {
          // Flip state → "paid" BEFORE the trailing refresh(). Two reasons:
          //   1. React commits the state change and unmounts the polling
          //      view immediately, so the user sees "You're in" even if the
          //      final refresh is slow.
          //   2. If refresh() ran first and setUser() flipped the memoized
          //      context value, this effect would remount and cancel its
          //      own closure — setState("paid") would never fire. With the
          //      stable useCallback refresh from AuthContext this can't
          //      happen anymore, but ordering the setState before the
          //      network await keeps the guarantee even if the context is
          //      touched from elsewhere.
          if (cancelled) return;
          setState("paid");
          // Fire-and-forget the refresh — the paid view + hard-navigate on
          // "Open dashboard" both pull /auth/me again, so we don't need to
          // await it here.
          try { await refresh?.(); } catch { /* non-fatal */ }
          return;
        }
      } catch { /* transient — keep polling */ }
      if (unlocked) {
        if (cancelled) return;
        setState("paid");
        return;
      }

      // Give up polling after ~90s (30 tries × 3s). Webhook can still land
      // later — the escape hatch below will do one final refresh.
      if (pollCount.current >= 30) {
        try {
          const refreshed = await refresh?.();
          const s = refreshed?.user?.subscription_status;
          if (s === "active" || s === "trialing" || s === "past_due") {
            if (!cancelled) setState("paid");
            return;
          }
        } catch { /* non-fatal */ }
        if (!cancelled) setState("failed");
        return;
      }
      setTimeout(poll, 3000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps -- `refresh` is stable (useCallback empty deps in AuthContext); listing it here caused this very bug — every refresh() call remounted the effect and cancelled its own polling closure before setState("paid") could commit.

  return (
    <div className="mx-auto max-w-[720px] p-6 md:p-12" data-testid="subscribe-success">
      {state === "polling" && (
        <div className="border border-border p-8 md:p-12 text-center">
          <Loader2 className="mx-auto animate-spin text-[#C9A85F]" size={28} />
          <p className="mono mt-4 text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            confirming payment
          </p>
          <h1 className="mt-3 text-2xl tracking-tight">
            Locking in your subscription…
          </h1>
        </div>
      )}
      {state === "paid" && (
        <div className="border border-[#C9A85F] p-8 md:p-12">
          <div className="inline-flex items-center gap-2 border border-[#C9A85F] px-3 py-1.5">
            <Check size={12} className="text-[#C9A85F]" />
            <span className="mono text-[10px] uppercase tracking-[0.25em] text-[#C9A85F]">
              subscription active
            </span>
          </div>
          <h1 className="mt-6 text-4xl leading-tight tracking-tight md:text-5xl">
            You're in.
          </h1>
          <p className="slogan gold-metallic-text mt-4 text-2xl md:text-3xl">
            Welcome to Book Away Pro.
          </p>
          <p className="mt-6 max-w-md text-sm text-muted-foreground">
            Your dashboard is unlocked. Post openings, manage bookings, and
            we'll email you when customers request slots.
          </p>
          <button
            type="button"
            data-testid="subscribe-success-cta"
            onClick={async () => {
              // Belt-and-braces: refresh once more so /auth/me reflects
              // the activated user, then hard-reload into /dashboard so
              // React state can't leak a stale user object into the
              // paywall gate.
              try { await refresh?.(); } catch { /* non-fatal */ }
              window.location.assign("/dashboard");
            }}
            className="mt-8 gold-metallic-bg px-5 py-3 text-sm font-semibold uppercase tracking-widest text-black"
          >
            Open dashboard
          </button>
        </div>
      )}
      {state === "failed" && (
        <div className="border border-border p-8 md:p-12">
          <h1 className="text-2xl tracking-tight">
            Hmm, we're still waiting on Stripe.
          </h1>
          <p className="mt-4 text-sm text-muted-foreground">
            Your payment may still land in a minute — check your inbox for a
            Stripe receipt, or click the button below to try your dashboard
            (we'll re-check your subscription there).
            If nothing shows up, email hello@bookaway.com.au and we'll sort it.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              data-testid="subscribe-failed-retry"
              onClick={async () => {
                // Force a fresh backend check: hitting /payments/status
                // makes the backend re-query Stripe and activate the user
                // via _mark_paid_and_activate if the webhook has landed
                // since the poll gave up.
                if (sessionId) {
                  try { await api.get(`/payments/status/${sessionId}`); }
                  catch { /* non-fatal */ }
                }
                // Durable safety net: if the webhook crashed mid-flight
                // and the transaction was already marked paid, the
                // status endpoint above would silently no-op. This
                // reconcile endpoint bypasses the transaction row
                // entirely — it looks up the user's Stripe subscription
                // directly and activates them.
                try { await api.post("/payments/reconcile"); }
                catch { /* non-fatal — user may not have a stripe customer yet */ }
                try { await refresh?.(); } catch { /* non-fatal */ }
                // Hard-reload so the paywall gate re-reads /auth/me on boot.
                window.location.assign("/dashboard");
              }}
              className="mono text-[10px] uppercase tracking-widest border border-foreground px-4 py-2 hover:bg-foreground hover:text-background transition-colors"
            >
              Try dashboard
            </button>
            <Link
              to="/dashboard/subscribe"
              className="mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
            >
              back to pricing
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export function SubscribeCancel() {
  return (
    <div className="mx-auto max-w-[560px] p-6 md:p-12" data-testid="subscribe-cancel">
      <div className="border border-border p-8 md:p-12 text-center">
        <div className="mx-auto flex h-10 w-10 items-center justify-center border border-border">
          <X size={16} className="text-muted-foreground" />
        </div>
        <h1 className="mt-6 text-3xl tracking-tight">
          No worries — no charge.
        </h1>
        <p className="mt-4 text-sm text-muted-foreground">
          You closed the checkout before completing payment. Nothing was
          charged, and your account state is unchanged.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/dashboard/subscribe"
            data-testid="subscribe-cancel-retry"
            className="gold-metallic-bg px-5 py-3 text-sm font-semibold uppercase tracking-widest text-black"
          >
            Try again
          </Link>
          <Link
            to="/dashboard"
            className="mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
          >
            ← back to dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}
